import { takeEvery, put, all, select } from 'redux-saga/effects';
import { toastr } from 'react-redux-toastr';

import * as types from '../../actions/candidate/candidate-references/types';
import * as actions from '../../actions/candidate/candidate-references';

import { loaderDecorator } from '../decorators/loader-decorator';

import httpStatusCodes from '../../constants/http-status-codes';
import candidateReferenceStatuses from '../../constants/candidate-reference-statuses';
import {
  maxCandidateReferencesCountLimit,
  minCandidateReferencesCountToApply
} from '../../constants/candidate-references';

import handleError from '../../tools/error-helper';
import apiFetcher from '../../tools/api-fetcher';

function* getCandidateReferences() {
  try {
    const { data } = yield apiFetcher
      .auth()
      .get(`candidates/current/references`);

    yield put(actions.setCandidateReferences(data));

    const authUser = yield select(state => state.authUser);
    const userIsNotSearchAssociates = !authUser.get('searchAssociatesId');

    const countedCandidateReferences = data.filter(
      candidateReference =>
        candidateReference.candidateReferenceStatus !==
        candidateReferenceStatuses.declined
    );

    const limitIsReached =
      userIsNotSearchAssociates &&
      countedCandidateReferences.length >= maxCandidateReferencesCountLimit;

    yield put(actions.setCandidateReferencesLimitReached(limitIsReached));

    const notEnoughReferencesToApply =
      userIsNotSearchAssociates &&
      data.length < minCandidateReferencesCountToApply;

    yield put(
      actions.setNotEnoughReferencesToApply(notEnoughReferencesToApply)
    );
  } catch (exception) {
    handleError(exception);
  }
}

function* submitNewCandidateReferenceRequest({ request, onSuccess }) {
  try {
    yield put(actions.resetSubmitNewCandidateReferenceRequestError());
    yield apiFetcher.auth().post(`candidates/current/references`, request);
    yield put(actions.setDisplayNewCandidateReferenceRequestForm(false));
    yield getCandidateReferences();
    onSuccess();
  } catch (exception) {
    switch (exception.response && exception.response.status) {
      case httpStatusCodes.Conflict: {
        yield put(
          actions.setSubmitNewCandidateReferenceRequestError(
            exception.response.statusText
          )
        );
        break;
      }
      //TODO 422 validation handling
      default: {
        handleError(exception);
        break;
      }
    }
  }
}

function* resendCandidateReferenceNotification({ candidateReferenceId }) {
  try {
    yield apiFetcher
      .auth()
      .put(`candidates/current/references/${candidateReferenceId}`);
    yield put(actions.setDisplayNewCandidateReferenceRequestForm(false));
    toastr.success('Candidate reference notification was resent.');
  } catch (exception) {
    switch (exception.response.status) {
      case httpStatusCodes.Conflict: {
        toastr.warning(
          'Request has been already completed. Please wait while references are refreshed.'
        );
        yield put(actions.getCandidateReferences());
        break;
      }
      default: {
        handleError(exception);
        break;
      }
    }
  }
}

function* removeCandidateReference({ candidateReferenceId, onSuccess }) {
  try {
    yield apiFetcher
      .auth()
      .delete(`candidates/current/references/${candidateReferenceId}`);
    yield getCandidateReferences();
    onSuccess();
    toastr.success('Candidate reference was removed.');
  } catch (exception) {
    handleError(exception);
  }
}

export default function* candidateReferencesSaga() {
  yield all([
    takeEvery(
      types.CANDIDATE_REFERENCES_GET_REFERENCES,
      loaderDecorator(
        getCandidateReferences,
        actions.setCandidateReferencesLoading
      )
    ),
    takeEvery(
      types.CANDIDATE_REFERENCES_SUBMIT_NEW_CANDIDATE_REFERENCE_REQUEST,
      loaderDecorator(
        submitNewCandidateReferenceRequest,
        actions.setCandidateReferencesLoading
      )
    ),
    takeEvery(
      types.CANDIDATE_REFERENCES_RESEND_NOTIFICATION,
      loaderDecorator(
        resendCandidateReferenceNotification,
        actions.setCandidateReferencesLoading
      )
    ),
    takeEvery(
      types.CANDIDATE_REFERENCES_REMOVE_CANDIDATE_REFERENCES,
      loaderDecorator(
        removeCandidateReference,
        actions.setCandidateReferencesLoading
      )
    )
  ]);
}



// WEBPACK FOOTER //
// ./src/sagas/candidate/candidate-references.js